import { BehaviorSubject, lastValueFrom, filter, take } from 'rxjs';

const storage = [];
const internalStorage = new WeakMap();

/**
 * Used to actually attach the state to the `BaseYCService`
 *
 * @param stateClass The class of the state to be instantiated and attached
 */
function AttachYCState(stateClass) {
    return function (constructor) {
        const state = internalStorage.get(stateClass) || Reflect.construct(stateClass, []);
        internalStorage.set(stateClass, state);
        Object.defineProperty(constructor.prototype, 'state', {
            get() {
                return state;
            }
        });
        return constructor;
    };
}

/**
 * Core functionality of the states
 */
class BaseState {
    constructor() {
        this.observableMap = {};
        if (internalStorage.get(this.constructor)) {
            return internalStorage.get(this.constructor);
        }
        else {
            internalStorage.set(this.constructor, this);
        }
    }
    /**
     * Returns an observable of changes to `attr`
     *
     * @param attr The property being observed
     * @returns Observable of changes to the attribute
     */
    changesTo$(attr) {
        return this.observableMap[attr] = (this.observableMap[attr] || new BehaviorSubject(this[attr]));
    }
    /**
     * Returns a promise waiting for the value of `attr` to be truthy
     *
     * @param attr The attribute to wait for
     * @returns A promise of the value for attr
     */
    waitFor(attr) {
        return lastValueFrom(this.changesTo$(attr)
            .pipe(filter((val) => !!val))
            .pipe(take(1)));
    }
    setAttr(attr, val) {
        this[attr] = val;
        this.changesTo$(attr);
        setTimeout(() => {
            this.observableMap[attr].next(val);
        });
        return this;
    }
    /**
     * Triggers the internal observable for `attr` to potentially trigger any subscribers
     *
     * @param attr The attribute to trigger
     */
    triggerChange(attr) {
        this.setAttr(attr, this[attr]);
    }
}

/**
 * The state can only be mutated internally, e.g. expose methods that perform mutation
 *
 * Useful for directly injecting states in multiple places
 */
class BaseYCActionState extends BaseState {
    set(attr, val) {
        return this.setAttr(attr, val);
    }
}

/**
 * Base service that exposes state functionality in a protected way
 */
class BaseYCService {
    /**
     * Updates the value on the state and triggers the observable. Only accessible within this service
     *
     * @param attr Attribute to update
     * @param val Value to set to
     * @returns this
     */
    set(attr, val) {
        return this.state.set(attr, val);
    }
    /**
     * Returns an observable for changees to `attr`
     *
     * @param attr Attribute to watch
     * @returns An observable
     */
    changesTo$(attr) {
        return this.state.changesTo$(attr);
    }
    /**
     * Returns a promise waiting for the value of `attr` on the state to be truthy
     *
     * @param attr The attribute to wait for
     * @returns A promise of the value for attr
     */
    waitFor(attr) {
        return this.state.waitFor(attr);
    }
    /**
     * Returns the current value from the state of `attr`
     *
     * @param attr The attribute to get
     * @returns The current value
     */
    get(attr) {
        return this.state[attr];
    }
    /**
     * Triggers a change for `attr` potentially causing change detection to happen downstream
     *
     * @param attr The attribute to triggere
     */
    triggerChange(attr) {
        this.state.triggerChange(attr);
    }
}

/**
 * This version allows the state to be mutated externally through the `set` method
 *
 * useful for use with `BaseYCService` and `InjectYCState`
 */
class BaseYcState extends BaseState {
    set(attr, val) {
        return this.setAttr(attr, val);
    }
}

/**
 * Registers the state for use
 */
function RegisterYCState() {
    return (Input) => {
        storage.push(Input);
        return Input;
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { AttachYCState, BaseYCActionState, BaseYCService, BaseYcState, RegisterYCState };
